import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import OverlayDialog from "./OverlayDialog.jsx";
import { RUN_CONSENT_REFUSAL } from "../lib/runConsent.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

const RunConsentBrokerContext = createContext(null);

/**
 * Application-level UI broker for one-run capability approval.
 *
 * Merely mounting this provider grants nothing. Interactive call sites obtain
 * the broker with `useRunConsentBroker()` and attach it to a consent context
 * created inside the user's click/submit handler. Automated callers never see
 * a dialog because they do not attach that context.
 */
export default function RunConsentProvider({ children }) {
    const queueRef = useRef([]);
    const currentRef = useRef(null);
    const mountedRef = useRef(true);
    const [currentRequest, setCurrentRequest] = useState(null);

    const showNext = useCallback(() => {
        if (!mountedRef.current) return;
        if (currentRef.current || queueRef.current.length === 0) return;
        const next = queueRef.current.shift();
        currentRef.current = next;
        setCurrentRequest(next.request);
    }, []);

    const requestConsent = useCallback((request) => new Promise((resolve) => {
        if (!mountedRef.current) {
            resolve({
                approved: false,
                reason: RUN_CONSENT_REFUSAL.BROKER_FAILED,
            });
            return;
        }
        if (request?.signal?.aborted) {
            resolve({
                approved: false,
                reason: RUN_CONSENT_REFUSAL.CANCELLED,
            });
            return;
        }

        const pending = {
            request,
            resolve,
            settled: false,
            onAbort: null,
        };
        if (request?.signal) {
            pending.onAbort = () => {
                if (pending.settled) return;
                const wasCurrent = currentRef.current === pending;
                if (wasCurrent) {
                    currentRef.current = null;
                    if (mountedRef.current) setCurrentRequest(null);
                } else {
                    const index = queueRef.current.indexOf(pending);
                    if (index >= 0) queueRef.current.splice(index, 1);
                }
                resolvePending(pending, {
                    approved: false,
                    reason: RUN_CONSENT_REFUSAL.CANCELLED,
                });
                if (wasCurrent) queueMicrotask(showNext);
            };
            request.signal.addEventListener("abort", pending.onAbort, {
                once: true,
            });
        }
        queueRef.current.push(pending);
        queueMicrotask(showNext);
    }), [showNext]);

    const settle = useCallback((approved) => {
        const pending = currentRef.current;
        if (!pending) return;
        currentRef.current = null;
        setCurrentRequest(null);
        resolvePending(pending, {
            approved,
            reason: approved ? undefined : RUN_CONSENT_REFUSAL.DECLINED,
        });
        queueMicrotask(showNext);
    }, [showNext]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            const refusal = {
                approved: false,
                reason: RUN_CONSENT_REFUSAL.BROKER_FAILED,
            };
            const current = currentRef.current;
            currentRef.current = null;
            if (current) resolvePending(current, refusal);
            for (const pending of queueRef.current.splice(0)) {
                resolvePending(pending, refusal);
            }
        };
    }, []);

    const broker = useMemo(() => ({ requestConsent }), [requestConsent]);

    return (
        <RunConsentBrokerContext.Provider value={broker}>
            {children}
            <RunConsentDialog
                request={currentRequest}
                onApprove={() => settle(true)}
                onDecline={() => settle(false)}
            />
        </RunConsentBrokerContext.Provider>
    );
}

export function useRunConsentBroker() {
    return useContext(RunConsentBrokerContext);
}

function RunConsentDialog({ request, onApprove, onDecline }) {
    if (!request || typeof document === "undefined") return null;

    const capability = request.capability || {};
    const isTool = capability.kind === "mcp_tool";
    const title = isTool ? "Allow this tool call?" : "Install this Python package?";
    const triggerLabel = request.trigger?.kind === "user"
        ? "User request"
        : "Model reason";

    return createPortal(
        <OverlayDialog
            open
            onClose={onDecline}
            analyticsId={DIALOG.RUN_CONSENT}
            eyebrow="Approve for this run"
            title={title}
            width="560px"
        >
            <div className="grid gap-[14px]">
                <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                    {isTool
                        ? "This call uses your live third-party connection. Review the exact destination and arguments before allowing it."
                        : "This package is not in Origin’s verified built-in set. It will be fetched by the parent and injected only into this isolated run."}
                </p>

                <dl className="m-0 grid grid-cols-[112px_minmax(0,1fr)] gap-x-[10px] gap-y-[8px] text-[11px] leading-[1.4]">
                    {isTool ? (
                        <>
                            <ConsentTerm>Server</ConsentTerm>
                            <ConsentValue mono>{capability.server}</ConsentValue>
                            <ConsentTerm>Tool</ConsentTerm>
                            <ConsentValue mono>{capability.tool}</ConsentValue>
                        </>
                    ) : (
                        <>
                            <ConsentTerm>Package</ConsentTerm>
                            <ConsentValue mono>
                                {capability.packageName}
                                {capability.version ? `==${capability.version}` : ""}
                            </ConsentValue>
                        </>
                    )}
                    <ConsentTerm>{triggerLabel}</ConsentTerm>
                    <ConsentValue>{request.trigger?.text}</ConsentValue>
                </dl>

                {isTool ? (
                    <div className="grid gap-[5px]">
                        <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
                            Resolved arguments
                        </span>
                        <pre className="m-0 max-h-[220px] overflow-auto whitespace-pre-wrap break-words rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] p-[10px] font-mono text-[10.5px] leading-[1.4] text-[color:var(--ink)]">
                            {safeStringify(capability.arguments)}
                        </pre>
                    </div>
                ) : capability.reason ? (
                    <div className="grid gap-[5px]">
                        <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
                            Import reason
                        </span>
                        <p className="m-0 whitespace-pre-wrap break-words text-[11px] leading-[1.4] text-[color:var(--ink)]">
                            {capability.reason}
                        </p>
                    </div>
                ) : null}

                <div className="flex items-center justify-end gap-[8px]">
                    <button
                        type="button"
                        onClick={onDecline}
                        autoFocus
                        className="h-[30px] cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                    >
                        Refuse
                    </button>
                    <button
                        type="button"
                        onClick={onApprove}
                        className="h-[30px] cursor-pointer rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] font-medium uppercase tracking-[0.04em] text-[color:var(--ink)] hover:border-[color:var(--green)]"
                    >
                        Allow once
                    </button>
                </div>
            </div>
        </OverlayDialog>,
        document.body,
    );
}

function ConsentTerm({ children }) {
    return (
        <dt className="m-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
            {children}
        </dt>
    );
}

function ConsentValue({ children, mono = false }) {
    return (
        <dd className={`m-0 min-w-0 whitespace-pre-wrap break-words text-[color:var(--ink)] ${mono ? "font-mono" : ""}`}>
            {children || "—"}
        </dd>
    );
}

function safeStringify(value) {
    try {
        return JSON.stringify(value ?? {}, null, 2);
    } catch {
        return String(value);
    }
}

function resolvePending(pending, result) {
    if (!pending || pending.settled) return;
    pending.settled = true;
    if (pending.onAbort && pending.request?.signal) {
        pending.request.signal.removeEventListener("abort", pending.onAbort);
    }
    pending.resolve(result);
}
